import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { Button, Form } from 'react-bootstrap';

const ListaDicionario = forwardRef(function ListaDicionario(
  {
    title,
    items,
    keyName = 'DESCRICAO',
    codeKey = 'COD',
    editable = false,
    onChangeItem,
    onChangeCode,
    onResolveCode,
    onAddItem,
    onRemoveItem,
    resolvingKeySet,
    onEnterPress,
  },
  ref
) {
  const resolveTimersRef = useRef({});
  // rowRefs.current[index] = HTMLInputElement do código daquela linha
  const rowRefs = useRef([]);
  // Guarda uma ação pendente a ser executada assim que a próxima renderização
  // disponibilizar a linha-alvo (ex: focar a linha recém-criada).
  const pendingFocusIndexRef = useRef(null);

  useEffect(
    () => () => {
      Object.values(resolveTimersRef.current).forEach((timerId) => {
        if (timerId) clearTimeout(timerId);
      });
      resolveTimersRef.current = {};
    },
    []
  );

  // Sempre que a lista de itens muda (ex: nova linha adicionada), se houver
  // um foco pendente, aplica nesse momento — já que o input daquela linha
  // só existe no DOM depois do próximo render.
  useEffect(() => {
    if (pendingFocusIndexRef.current === null) return;
    const targetIndex = pendingFocusIndexRef.current;
    pendingFocusIndexRef.current = null;

    requestAnimationFrame(() => {
      rowRefs.current[targetIndex]?.focus?.();
    });
  }, [items]);

  useImperativeHandle(
    ref,
    () => ({
      // Chamado de fora (FormEcodoppler) quando o ENTER "entra" nesta lista.
      focusFirstRow: () => {
        if (!items?.length) {
          pendingFocusIndexRef.current = 0;
          onAddItem?.();
          return;
        }
        requestAnimationFrame(() => {
          rowRefs.current[0]?.focus?.();
        });
      },
    }),
    [items, onAddItem]
  );

  if (!items?.length && !editable) return null;

  const handleResolveCode = (index, rawCode, targetCodeKey = codeKey) => {
    const normalizedCode = String(rawCode || '').trim();
    onResolveCode?.(index, normalizedCode, targetCodeKey);
  };

  const scheduleResolveCode = (index, rawCode, targetCodeKey = codeKey) => {
    const normalizedCode = String(rawCode || '').trim();
    const timerKey = `${index}:${targetCodeKey}`;

    if (resolveTimersRef.current[timerKey]) {
      clearTimeout(resolveTimersRef.current[timerKey]);
    }

    if (!normalizedCode) {
      onResolveCode?.(index, '', targetCodeKey);
      return;
    }

    resolveTimersRef.current[timerKey] = setTimeout(() => {
      handleResolveCode(index, normalizedCode, targetCodeKey);
      delete resolveTimersRef.current[timerKey];
    }, 450);
  };

  // ENTER: confirma o código (como já fazia) e decide entre remover a linha
  // vazia ou avançar para o próximo campo/lista da tela.
  const handleEnterOnRow = (index, rawCode) => {
    const normalizedCode = String(rawCode || '').trim();
    const timerKey = `${index}:${codeKey}`;
    if (resolveTimersRef.current[timerKey]) {
      clearTimeout(resolveTimersRef.current[timerKey]);
      delete resolveTimersRef.current[timerKey];
    }
    handleResolveCode(index, normalizedCode, codeKey);

    if (!normalizedCode) {
      onRemoveItem?.(index);
    }

    onEnterPress?.();
  };

  // Seta para baixo: sempre cria uma nova linha abaixo e foca nela
  // (replica o comportamento do DBGrid do Delphi 7).
  const handleArrowDownOnRow = (index) => {
    pendingFocusIndexRef.current = index + 1;
    onAddItem?.();
  };

  return (
    <div className="legacy-list mb-3">
      {title ? (
        <div className="legacy-list-header d-flex align-items-center justify-content-between">
          <strong>{title}</strong>
          {editable && (
            <Button
              size="sm"
              variant="outline-primary"
              className="legacy-list-add"
              onClick={() => onAddItem?.()}
            >
              + Adicionar
            </Button>
          )}
        </div>
      ) : null}
      <div className="legacy-list-body">
        {(items || []).map((item, index) => (
          <div className="legacy-list-row" key={`${keyName}-${index}`}>
            <div className="legacy-list-code-wrap">
              {editable ? (
                <Form.Control
                  type="text"
                  ref={(el) => {
                    rowRefs.current[index] = el;
                  }}
                  value={item?.[codeKey] ?? ''}
                  onChange={(e) => {
                    onChangeCode?.(index, e.target.value, codeKey);
                    scheduleResolveCode(index, e.target.value, codeKey);
                  }}
                  onBlur={(e) => {
                    const timerKey = `${index}:${codeKey}`;
                    if (resolveTimersRef.current[timerKey]) {
                      clearTimeout(resolveTimersRef.current[timerKey]);
                      delete resolveTimersRef.current[timerKey];
                    }
                    handleResolveCode(index, e.target.value, codeKey);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleEnterOnRow(index, e.currentTarget.value);
                    } else if (e.key === 'ArrowDown') {
                      e.preventDefault();
                      handleArrowDownOnRow(index);
                    }
                  }}
                  className="legacy-input legacy-list-code-input"
                  aria-label={`${title || keyName} código ${index + 1}`}
                />
              ) : (
                <div className="legacy-list-code">{item?.[codeKey] ?? index + 1}</div>
              )}
            </div>
            {editable ? (
              <div className="legacy-list-text legacy-list-text-readonly">
                {item?.[keyName] ?? ''}
                {resolvingKeySet?.has(`${index}`) ? (
                  <span className="legacy-list-resolving"> buscando...</span>
                ) : null}
              </div>
            ) : (
              <div className="legacy-list-text">{item[keyName]}</div>
            )}
            {editable && (
              <div className="legacy-list-remove-wrap">
                <Button
                  size="sm"
                  variant="outline-danger"
                  className="legacy-list-remove"
                  onClick={() => onRemoveItem?.(index)}
                >
                  -
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

export default ListaDicionario;
